import { DataService } from 'api/DataService'

import { onlyNumber } from './FormatMoney'

export interface CEPResponse {
  cep: string
  logradouro: string
  complemento: string
  unidade: string
  bairro: string
  localidade: string
  uf: string
  estado: string
  regiao: string
  ibge: string
  gia: string
  ddd: string
  siafi: string
  error?: boolean
  message?: string
}

type CEPLookup = {
  data: CEPResponse
  // Só resposta definitiva (encontrado ou CEP inválido) fica guardada; falha de
  // rede/5xx tem que poder tentar de novo na próxima chamada.
  cacheable: boolean
}

const emptyCEP = (cep: string): CEPResponse => ({
  cep: cep,
  logradouro: '',
  complemento: '',
  unidade: '',
  bairro: '',
  localidade: '',
  uf: '',
  estado: '',
  regiao: '',
  ibge: '',
  gia: '',
  ddd: '',
  siafi: ''
})

// SC-11279: o schema yup valida o CEP chamando a API, e o react-hook-form
// revalida o schema inteiro a cada tecla depois do primeiro submit. Sem guardar
// o resultado, cada tecla em qualquer campo virava um GET /v1/cep (centenas por
// sessão, o que levou a ViaCEP a limitar o IP da API). Guarda por CEP e
// reaproveita a requisição em andamento.
const cepRequests = new Map<string, Promise<CEPLookup>>()

const requestCEPData = async (
  cep: string,
  cleanCep: string
): Promise<CEPLookup> => {
  try {
    const response = await DataService({
      url: `/v1/cep/${cleanCep}`,
      type: 'GET'
    })

    if (response.error) {
      return {
        data: {
          ...emptyCEP(cep),
          error: true,
          message: response.message || 'CEP inválido.'
        },
        cacheable: response.status === 400
      }
    }

    return { data: response.data, cacheable: true }
  } catch (error) {
    return { data: emptyCEP(cep), cacheable: false }
  }
}

export const getCEPData = async (cep: string): Promise<CEPResponse> => {
  const cleanCep = onlyNumber(cep)

  if (cleanCep.length !== 8) {
    return { ...emptyCEP(cep), error: true, message: 'CEP inválido.' }
  }

  let request = cepRequests.get(cleanCep)
  if (!request) {
    request = requestCEPData(cep, cleanCep)
    cepRequests.set(cleanCep, request)
  }

  const { data, cacheable } = await request
  if (!cacheable && cepRequests.get(cleanCep) === request) {
    cepRequests.delete(cleanCep)
  }

  return data
}
